<script setup>
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { getBoardName } from '@/composables/functions';
import IconHeaderBack from '../svg/IconHeaderBack.vue';
import IconMenu from '../svg/IconMenu.vue';
import { IconNotice, IconEssay, IconEvent, IconFaq, IconCounseling } from '@/components/svg';
import MypageLnbTabMenu from '@/components/MypageLnbTabMenu.vue';
import { gnbMenuLinks } from '@/constants/menuLinks';
import { gnbContentTypeMap as contentTypeMap } from '@/constants/logContentType';
import { nangmanCenterContentTypeMap as nmcContentTypeMap } from '@/constants/logContentType';
import { addContentLogAPI } from '@/api/logServiceApi';
import { useMyInfoStore } from '@/store/modules/myinfo';
import { clearMySajuState } from '@/composables/calendar/mySaju/useMySaju';
import DropdownCreditInfo from '@/components/common/DropdownCreditInfo.vue';

definePage({ meta: { layout: 'customer' } });

const creditInfoRef = ref(null);
const props = defineProps({
  board: {
    type: String,
    default: '',
  },
  pageType: {
    type: String,
    default: '',
  },
  showBack: {
    type: Boolean,
    default: false,
  },
});
console.log(props.board);

const route = useRoute();
const router = useRouter();
const store = useStore();
const myInfoStore = useMyInfoStore();

const isWebView = ref(false);

onMounted(() => {
  isWebView.value = !!window.ReactNativeWebView;
});

// 모바일 뒤로가기
/** 이전 페이지 검사 */
function hasBackNow() {
  const st = window.history.state || null;
  if (st && typeof st.position === 'number') return st.position > 0;
  if (st && st.back != null) return true;
  return false;
}
/** 뒤로가기 로직 */
function onBack() {
  if (hasBackNow()) {
    router.back();
  } else {
    if (window.__APP_VERSION__) {
      router.push('/main');
      return;
    }
  }
}

// --- 햄버거 메뉴 (낭만센터 탭 전환, 모바일 웹 전용) ---
const nmcSubMenus = gnbMenuLinks['nangman-center'].subMenu;
const isNmcMenuOpen = ref(false);

const toggleNmcMenu = () => {
  isNmcMenuOpen.value = !isNmcMenuOpen.value;
  if (isMypageMenuOpen.value) isMypageMenuOpen.value = false;
};

const closeNmcMenu = () => {
  isNmcMenuOpen.value = false;
};

const isNmcMenuActive = (menuLink) => {
  return route.path.startsWith(menuLink);
};

const handleNmcMenuClick = (menu) => {
  const ct = nmcContentTypeMap[menu.link];
  if (ct) addContentLogAPI(ct);
  router.push(menu.link);
  closeNmcMenu();
};

// --- 마이페이지 드롭다운 메뉴 (모바일 웹 전용) ---
const isMypageMenuOpen = ref(false);

const toggleMypageMenu = () => {
  isMypageMenuOpen.value = !isMypageMenuOpen.value;
  if (isNmcMenuOpen.value) isNmcMenuOpen.value = false;
};

const closeMypageMenu = () => {
  isMypageMenuOpen.value = false;
};

watch(
  () => route.path,
  () => {
    if (isMypageMenuOpen.value) isMypageMenuOpen.value = false;
    if (isNmcMenuOpen.value) isNmcMenuOpen.value = false;
  },
);

const isLoggedIn = computed(() => {
  const userId = store.getters['user/id'];
  const userName = store.getters['user/name'];
  try {
    const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || 'null');
    return !!(userId || userName || userInfo?.name);
  } catch {
    return !!(userId || userName);
  }
});

const displayName = computed(() => {
  if (isLoggedIn.value) {
    return myInfoStore?.displayName || store.getters['user/name'] || '';
  }
  return '';
});
const isHomeRoute = computed(() => route.path === '/main' || route.path === '/');

const handleGoHome = () => {
  closeMypageMenu();
  if (!isHomeRoute.value) {
    router.push('/main');
  }
};

const handleLogin = () => {
  addContentLogAPI('gnb_btn_join');
  router.push({
    path: '/auth/login',
    query: { to: route.fullPath },
  });
  closeMypageMenu();
};

const isMenuActive = (menuLink) => {
  const currentPath = route.path;
  switch (menuLink.name) {
    case '낭만분석':
      return (
        currentPath.startsWith('/saju-service') ||
        currentPath.startsWith('/saju-result') ||
        currentPath.startsWith('/saju-insight') ||
        currentPath.startsWith('/analysis')
      );
    default:
      return currentPath.startsWith(menuLink.basePath || menuLink.link);
  }
};

const handleMenuClick = (menu) => {
  addContentLogAPI(contentTypeMap[menu.link]);
  if (menu.name !== '낭만만세력은?') {
    router.push(menu.link);
  }
  closeMypageMenu();
};

const handleLogout = async () => {
  addContentLogAPI('pi_btn_logout');
  try {
    await store.dispatch('user/logout');
    clearMySajuState();
    await router.push('/auth/login');
    myInfoStore.resetSelectedOption();
    closeMypageMenu();
  } catch (error) {
    console.error('Logout error:', error);
  }
};
</script>

<template>
  <div class="board-tit-box">
    <h3 class="h3">
      <span>{{ getBoardName(board, 'name') }}</span>
      <div v-if="board === 'faq'" class="txt-exp">
        낭만만세력 이용 중 궁금한 사항들에 대해서 확인할 수 있습니다.
      </div>
      <p v-if="pageType === 'write'" class="txt-exp">
        문의하신 내용은 1:1 내역보기에서 확인하실 수 있습니다.
      </p>
    </h3>

    <slot name="add-on"></slot>
  </div>
  <div v-if="!['product', 'inquiry', 'counseling'].includes(board)" class="app-board-header__fixed">
    <div class="app-board-header">
      <!-- 앱 웹뷰 또는 상세 페이지: 뒤로가기 버튼 -->
      <div v-if="isWebView || showBack" class="btn-left">
        <button class="btn-back" @click="onBack">
          <IconHeaderBack />
        </button>
      </div>
      <!-- 모바일 웹 목록: 햄버거 버튼 (왼쪽) -->
      <div v-if="!isWebView && !showBack" class="btn-left">
        <button class="btn-hamburger" @click="toggleNmcMenu">
          <IconMenu />
        </button>
      </div>
      <div class="tit">{{ getBoardName(board, 'name') }}</div>
      <!-- 모바일 웹: 사람 아이콘 (오른쪽) -->
      <div v-if="!isWebView" class="btn-right">
        <button class="btn-mypage" @click="toggleMypageMenu">
          <i class="icon-mypage"></i>
        </button>
      </div>
    </div>
    <slot name="add-on"></slot>
  </div>

  <!-- 낭만센터 탭 메뉴 드롭다운 (모바일 웹 전용) -->
  <Teleport to="body">
    <div v-if="isNmcMenuOpen && !isWebView" class="nmc-menu-overlay" @click="closeNmcMenu">
      <div class="nmc-menu-dropdown" @click.stop>
        <router-link
          v-for="(menu, index) in nmcSubMenus"
          :key="index"
          :to="menu.link"
          :class="{ active: isNmcMenuActive(menu.link) }"
          class="nmc-menu-item"
          @click="handleNmcMenuClick(menu)"
        >
          <IconNotice v-if="menu.icon === 'icon-notice'" />
          <IconCounseling v-if="menu.icon === 'icon-counseling'" />
          <IconEvent v-if="menu.icon === 'icon-event'" />
          <IconEssay v-if="menu.icon === 'icon-essay'" />
          <IconFaq v-if="menu.icon === 'icon-faq'" />
          {{ menu.name }}
        </router-link>
      </div>
    </div>
  </Teleport>

  <!-- 마이페이지 드롭다운 메뉴 (모바일 웹 전용) -->
  <Teleport to="body">
    <div v-if="isMypageMenuOpen && !isWebView" class="mypage-menu-overlay" @click="closeMypageMenu">
      <div class="mypage-menu-dropdown" @click.stop>
        <div class="login-msg-box" @click="creditInfoRef?.closeDropdown()">
          <button class="btn-home" :class="{ active: isHomeRoute }" @click="handleGoHome">
            <i class="icon-home"></i>
            홈
          </button>
          <template v-if="isLoggedIn">
            <div class="txt-box">
              <span
                ><span class="txt1" style="margin-right: 2px">{{ displayName || '게스트' }}</span
                >님</span
              >
              <span class="txt2">행복한 하루 되세요</span>
            </div>
          </template>
          <template v-else>
            <div class="txt-box">
              <span class="txt1">게스트</span>
              <span class="txt2">로그인 후 이용해주세요</span>
            </div>
          </template>

          <DropdownCreditInfo
            v-if="isLoggedIn"
            ref="creditInfoRef"
            :is-logged-in="isLoggedIn"
            :visible="isMypageMenuOpen"
            @close-menu="closeMypageMenu"
          />

          <div class="gnb-menu-list">
            <template v-for="(menu, key) in gnbMenuLinks" :key="key">
              <router-link
                v-if="menu.name !== '낭만만세력은?'"
                :to="menu.link"
                :class="{ active: isMenuActive(menu) }"
                class="gnb-menu-item"
                @click="handleMenuClick(menu)"
              >
                {{ menu.name }}
              </router-link>
              <a
                v-else
                :href="$router.resolve(menu.link).href"
                target="_blank"
                rel="noopener"
                class="gnb-menu-item"
                @click="handleMenuClick(menu)"
              >
                {{ menu.name }}
              </a>
            </template>
          </div>

          <template v-if="isLoggedIn">
            <MypageLnbTabMenu />
            <button class="btn-xs line" @click="handleLogout">로그아웃</button>
          </template>
          <template v-else>
            <button class="btn-xs line login-btn" @click="handleLogin">로그인</button>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>
<style lang="scss">
@import url('/src/assets/nangman_style/layout.scss');

.app-board-header .btn-right {
  position: absolute;
  right: 8px;
  top: calc((100% + var(--safe-area-top, 0px)) / 2);
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
}

.app-board-header .btn-right .btn-hamburger {
  display: flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.app-board-header .btn-right .btn-mypage {
  display: flex;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;

  .icon-mypage {
    display: flex;
    background: url('@manseImages/icon-mypage.png') center center no-repeat;
    background-size: 32px auto;
    width: 32px;
    height: 32px;
    border-radius: 100%;
    font-size: 0;
  }
}

@media (max-width: 768px) {
  .nmc-menu-overlay {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: transparent;
    z-index: 998;
  }

  .nmc-menu-dropdown {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 60px;
    left: 16px;
    min-width: 160px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 999;
    overflow: hidden;
    padding: 8px 0;

    .nmc-menu-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 20px;

      .icon {
        display: flex;
        align-items: center;
        line-height: 0;
      }
      color: #333;
      font-family: Pretendard;
      font-size: 15px;
      font-weight: 500;
      line-height: 20px;
      text-decoration: none;
      transition: all 0.2s;

      &:hover {
        background: #f5f5f5;
        color: #eb4c10;
      }

      &.active {
        color: #eb4c10;
        font-weight: 700;
        background: #fff5f2;
      }
    }
  }

  .mypage-menu-overlay {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: transparent;
    z-index: 998;
  }

  .mypage-menu-dropdown {
    display: block;
    position: fixed;
    top: 60px;
    right: 16px;
    width: 280px;
    max-height: calc(100vh - 76px);
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 999;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;

    .login-msg-box {
      position: relative;
      padding: 20px;
      width: 100%;
      box-sizing: border-box;

      .btn-home {
        position: absolute;
        top: 14px;
        right: 14px;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        height: 28px;
        padding: 0 8px;
        border: 1px solid #e6e6e6;
        border-radius: 14px;
        background: #fff;
        color: #333;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;

        .icon-home {
          width: 14px;
          height: 14px;
          background-color: currentColor;
          -webkit-mask: url('@manseImages/icon_home.svg') center center / contain no-repeat;
          mask: url('@manseImages/icon_home.svg') center center / contain no-repeat;
        }

        &.active {
          color: #eb4c10;
          border-color: #f7d1c3;
          background: #fff5f2;
        }
      }

      .txt-box {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        color: #333;
        font-size: 15px;
        margin-bottom: 16px;
        padding-top: 6px;

        .txt1 {
          font-weight: 700;
        }

        .txt2 {
          color: #757575;
          font-size: 13px;
          margin-top: 4px;
        }
      }

      .gnb-menu-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 16px;
        padding-top: 12px;
        border-top: 1px solid #f5f5f5;

        .gnb-menu-item {
          display: inline-block;
          padding: 6px 12px;
          color: #333;
          font-family: Pretendard;
          font-size: 13px;
          font-weight: 500;
          line-height: 20px;
          text-decoration: none;
          border-radius: 6px;
          background: #f9f9f9;
          transition: all 0.2s;
          white-space: nowrap;

          &:hover {
            background: #f0f0f0;
            color: #eb4c10;
          }

          &.active {
            background: #fff5f2;
            color: #eb4c10;
            font-weight: 600;
          }
        }
      }

      .btn-xs.line {
        width: 100%;
        margin-top: 8px;
        padding: 8px 16px;
        border: 1px solid #ebebeb;
        border-radius: 6px;
        background: #fff;
        color: #333;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
          background: #f5f5f5;
        }

        &.login-btn {
          background: var(--orange-300-main, #eb4c10);
          color: #fff;
          border-color: var(--orange-300-main, #eb4c10);

          &:hover {
            background: #d43d0e;
            border-color: #d43d0e;
          }
        }
      }
    }
  }
}
</style>
